import { api, mutate, readCache, type Ack } from '../api'
import { routeGen } from '../route-state'
import { getMe, loadMe, worksAlone } from '../me'
import { clear, el, loadError, money, toast } from '../ui'
import { currencySymbol } from '../round-config'
import { rowMenu } from '../row-menu'

// People (owner) — who can sign in to this round: contract §5 (/api/users)
// and §8. Everyone on this list signs in with a one-time code sent to their
// email; there is no password to set, share or reset.

export interface PersonRow {
  id: number
  email: string
  role: 'owner' | 'worker'
  worker_name: string | null
  // 1 = this worker sees job prices, 0 = hidden from them (0021). A new
  // worker starts at 0 (0022).
  show_prices?: number
  // How they are paid (0064): a day rate in pence, or a commission as a whole percent; null: not set.
  pay_kind?: 'day' | 'commission' | null
  pay_amount?: number | null
  pay_min_pence?: number | null
  version: number
}

let requestSequence = 0
let rows: PersonRow[] = []

// A one-person round (Liam, 19 Oct 2026): adding someone turns Working with others back on in the same save
// (functions/api/users/index.ts), so the app asks /api/me again and draws its screens for a team. An add that waited
// for signal does the same once it lands, whichever screen is on show.
if (typeof window !== 'undefined') {
  window.addEventListener('roundly:queued-landed', (e) => {
    const d = (e as CustomEvent<{ method?: string; path?: string }>).detail
    if (d?.method === 'POST' && d.path === '/api/users' && worksAlone()) void loadMe()
  })
}
const ADD_TURNS_ON = 'Adding someone turns on Working with others: the Diary shows who is out each day, Book a visit asks who is doing it, and Money shows worker pay.'
let editId: number | null = null
let confirmRemoveId: number | null = null

// api.ts only keeps the server's ERROR MESSAGE (the `code` field on a 409/400
// body is dropped before it reaches a screen — see the manager report), so
// the two named refusals are matched on the exact wording the server sends.
const VERSION_CONFLICT = 'Changed somewhere else first. Reload and try again.'
const LAST_OWNER = 'every business needs an owner'
const PLAIN_ERROR: Record<string, string> = {
  'that email already uses Roundly': 'That email already uses Roundly.',
  [LAST_OWNER]: 'Your round needs at least one owner.',
  'that email is a Roundly admin and cannot belong to a business': "That email is a Roundly admin sign-in, so it can't be added to a round.",
}

function plainError(message: string | undefined): string {
  if (!message) return "That didn't save. Try again."
  return PLAIN_ERROR[message] ?? message
}

function ownerCount(): number {
  return rows.filter((r) => r.role === 'owner').length
}

// ---- add ---------------------------------------------------------------

function renderAdd(root: HTMLElement | null, onAdded: (held: boolean) => void = () => undefined, reload: () => void = () => { if (root) void load(root) }): HTMLElement {
  const form = el('form', { class: 'add-form mb-add-form' })
  const err = el('p', { class: 'form-error', role: 'alert' })
  const email = el('input', { type: 'email', placeholder: 'name@example.com', required: 'required', 'aria-label': 'New person’s email' })
  const roleSel = el('select', { 'aria-label': 'Owner or worker' })
  // Worker first: the person an owner adds is usually someone who does jobs, and a wrong Owner would see prices,
  // money and bank details, while a wrong Worker costs one change (Liam, 29 Sep).
  roleSel.append(el('option', { value: 'worker' }, 'Worker'), el('option', { value: 'owner' }, 'Owner'))
  ;(roleSel as HTMLSelectElement).value = 'worker'
  const name = el('input', { placeholder: 'e.g. Sam', 'aria-label': 'Name that appears on their visits' })
  const nameHelp = el('p', { class: 'field-help' }, 'The name that shows on their visits, not their email.')
  const nameCaption = el('span', {}, 'Name on their visits')
  const nameField = el('label', { class: 'booking-field' }, nameCaption, name, nameHelp)

  // Owners who do jobs too get a name as well, and with it a Today tab
  // (Liam, 2026-09-25). Optional for an owner; a worker must have one.
  const updateNameField = (): void => {
    const isWorker = (roleSel as HTMLSelectElement).value === 'worker'
    nameCaption.textContent = isWorker ? 'Name on their visits' : 'Do they do jobs too? Name on their visits'
    nameHelp.textContent = isWorker ? 'The name that shows on their visits, not their email.' : 'Leave empty if they only run the office. With a name, they get a Today tab with their jobs. Spell it exactly as on their visits.'
  }
  roleSel.addEventListener('change', updateNameField)
  updateNameField()

  form.append(
    el('label', { class: 'booking-field' }, 'Email', email),
    el('label', { class: 'booking-field' }, 'Owner or worker', roleSel),
    nameField,
    err,
  )
  const submit = el('button', { type: 'submit', class: 'btn-primary' }, 'Add person')
  form.append(el('div', { class: 'people-add-acts' }, submit))

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    err.textContent = ''
    const isWorker = (roleSel as HTMLSelectElement).value === 'worker'
    const workerName = name.value.trim()
    if (isWorker && !workerName) {
      err.textContent = 'Type the name that appears on their visits.'
      return
    }
    submit.setAttribute('disabled', 'disabled')
    const gen = routeGen
    void mutate({
      label: `Add person: ${email.value}`,
      method: 'POST',
      path: '/api/users',
      body: { email: email.value.trim(), role: isWorker ? 'worker' : 'owner', worker_name: workerName || null },
    }).then((ack) => {
      // Left People meanwhile: nothing here to update, and #screen is another page now (Codex, 15 Oct 2026).
      if (gen !== routeGen) return
      if (ack.ok) {
        onAdded(false)
        if (worksAlone()) void loadMe()
        reload()
        return
      }
      // No signal: the add waits on this phone and goes by itself, so it is not offered again to tap twice.
      if (ack.status === 0 && !ack.signedOut && !ack.parked) {
        onAdded(true)
        return
      }
      submit.removeAttribute('disabled')
      if (ack.status >= 400 && ack.status < 500) {
        err.textContent = plainError(ack.error)
      } else {
        err.textContent = "Couldn't save yet. Check your connection. It will keep trying."
      }
    })
  })
  return form
}

// ---- one row -------------------------------------------------------------

function renderEditForm(row: PersonRow, root: HTMLElement): HTMLElement {
  const form = el('form', { class: 'add-form mb-edit-form' })
  const err = el('p', { class: 'form-error', role: 'alert' })
  const roleSel = el('select', { 'aria-label': `Owner or worker for ${row.email}` })
  roleSel.append(el('option', { value: 'owner' }, 'Owner'), el('option', { value: 'worker' }, 'Worker'))
  ;(roleSel as HTMLSelectElement).value = row.role
  const name = el('input', { placeholder: 'e.g. Sam', 'aria-label': `Name on visits for ${row.email}` })
  ;(name as HTMLInputElement).value = row.worker_name ?? ''
  const nameCaption = el('span', {}, 'Name on their visits')
  const nameHelp = el('p', { class: 'field-help' }, '')
  const nameField = el('label', { class: 'booking-field' }, nameCaption, name, nameHelp)

  // An owner can keep a name too (they do jobs, and get a Today tab). It must
  // match the name already on their visits; changing it does not move old
  // jobs. Their separate worker login, if they had one, can go once this
  // phone shows their jobs (Grok peer notes 2026-09-25: never both at once).
  // A one-person owner's own name stays while it is just them, and a new one takes their coming visits and days with it
  // (functions/api/users/[id].ts).
  const alone = worksAlone() && row.role === 'owner'
  const updateNameField = (): void => {
    const isWorker = (roleSel as HTMLSelectElement).value === 'worker'
    nameCaption.textContent = alone ? 'Name on your visits' : isWorker ? 'Name on their visits' : 'Do they do jobs too? Name on their visits'
    nameHelp.textContent = alone && !isWorker
      ? 'It stays while it is just you on this round. A new name moves your coming visits and working days to it.'
      : isWorker
      ? 'Spell it exactly as it is on their visits. Changing it does not move old jobs.'
      : 'Leave empty if they only run the office. With a name, they get a Today tab with their jobs. Spell it exactly as on their visits: changing it does not move old jobs. Had a separate worker login with this name? Remove it once your jobs show on Today.'
  }
  roleSel.addEventListener('change', updateNameField)
  updateNameField()

  form.append(
    el('p', { class: 'mb-row-email' }, row.email),
    el('label', { class: 'booking-field' }, 'Owner or worker', roleSel),
    nameField,
    err,
  )
  const save = el('button', { type: 'submit', class: 'btn-primary' }, 'Save')
  const cancel = el('button', { type: 'button', class: 'btn-secondary' }, 'Cancel')
  cancel.addEventListener('click', () => {
    editId = null
    render(root)
  })
  form.append(save, cancel)

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    err.textContent = ''
    const isWorker = (roleSel as HTMLSelectElement).value === 'worker'
    const workerName = (name as HTMLInputElement).value.trim()
    if (isWorker && !workerName) {
      err.textContent = 'Type the name that appears on their visits.'
      return
    }
    save.setAttribute('disabled', 'disabled')
    void mutate({
      label: `Change ${row.email}`,
      method: 'PATCH',
      path: `/api/users/${row.id}`,
      body: { role: isWorker ? 'worker' : 'owner', worker_name: workerName || null, version: row.version },
    }).then((ack) => {
      if (ack.ok) {
        editId = null
        // Your own name changed: the Today tab comes or goes with it, and the
        // top bar is only drawn at start-up, so start again.
        const me = getMe()
        if (me?.email && me.email.toLowerCase() === row.email.toLowerCase() && (row.worker_name ?? '') !== workerName) {
          window.location.reload()
          return
        }
        void load(root)
        return
      }
      save.removeAttribute('disabled')
      showFailure(ack, err, root)
    })
  })
  return form
}

// Shared refusal handling for both Save and Remove: the last-owner rule
// stays on screen (retrying won't change the answer), a genuine version
// conflict re-reads the list (someone else's edit landed), and anything
// else shows the server's own words, plain-worded where we have a mapping.
function showFailure(ack: Ack, err: HTMLElement, root: HTMLElement | (() => void)): void {
  if (ack.status === 409 && ack.error === LAST_OWNER) {
    err.textContent = plainError(LAST_OWNER)
    return
  }
  if (ack.status === 409 && ack.error === VERSION_CONFLICT) {
    err.textContent = 'Someone else changed this just now. Reloading the list.'
    if (typeof root === 'function') root()
    else void load(root)
    return
  }
  if (ack.status >= 400 && ack.status < 500) {
    err.textContent = plainError(ack.error)
    return
  }
  err.textContent = "Couldn't save yet. Check your connection. It will keep trying."
}

function renderRow(row: PersonRow, root: HTMLElement): HTMLElement {
  const item = el('div', { class: 'mb-row people-card' })
  if (editId === row.id) {
    item.classList.add('mb-row-editing')
    item.append(renderEditForm(row, root))
    return item
  }

  // A card for each person, the name on their visits first and their email small under it; Change and Remove in the
  // card's ⋯ (Liam picked A for People, 15 Oct 2026: https://claude.ai/artifact/9En31D1mDsYU6JyaLQrFQQ).
  const name = row.worker_name?.trim() || ''
  const lastOwner = row.role === 'owner' && ownerCount() <= 1
  const err = el('p', { class: 'form-error', role: 'alert' })
  const menu = rowMenu(`More for ${name || row.email}`, (list, close) => {
    const change = el('button', { type: 'button' }, 'Change name or role')
    change.addEventListener('click', () => { close(); editId = row.id; confirmRemoveId = null; render(root) })
    const remove = el('button', { type: 'button' }, 'Remove')
    if (lastOwner) remove.setAttribute('disabled', 'disabled')
    else remove.addEventListener('click', () => { close(); confirmRemoveId = row.id; render(root) })
    list.append(change, remove)
    if (lastOwner) list.append(el('p', { class: 'row-menu-label' }, 'Your round needs at least one owner. Add another owner before removing this one.'))
  })
  item.append(el('div', { class: 'people-head' },
    el('span', { class: `people-av people-av-${row.role}`, 'aria-hidden': 'true' }, (name || row.email).charAt(0).toUpperCase()),
    el('div', { class: 'people-who' }, el('strong', { class: 'people-name' }, name || row.email), ...(name ? [el('span', { class: 'people-email' }, row.email)] : [])),
    menu))
  item.append(el('div', { class: 'people-chips' }, el('span', { class: `mb-badge mb-badge-${row.role}` }, row.role === 'owner' ? 'Owner' : 'Worker'),
    ...(row.role === 'owner' && name ? [el('span', { class: 'mb-badge people-chip-jobs' }, 'Does jobs too')] : [])))

  if (confirmRemoveId === row.id) {
    const box = el('div', { class: 'mb-confirm' })
    box.append(el('p', {}, `Remove ${row.email}? They will no longer be able to sign in.`))
    const yes = el('button', { type: 'button', class: 'btn-remove' }, 'Yes, remove')
    const cancel = el('button', { type: 'button', class: 'btn-secondary' }, 'Cancel')
    cancel.addEventListener('click', () => {
      confirmRemoveId = null
      render(root)
    })
    yes.addEventListener('click', () => {
      yes.setAttribute('disabled', 'disabled')
      cancel.setAttribute('disabled', 'disabled')
      void mutate({
        label: `Remove person: ${row.email}`,
        method: 'DELETE',
        path: `/api/users/${row.id}?version=${row.version}`,
        body: {},
      }).then((ack) => {
        if (ack.ok) {
          confirmRemoveId = null
          void load(root)
          return
        }
        yes.removeAttribute('disabled')
        cancel.removeAttribute('disabled')
        showFailure(ack, err, root)
      })
    })
    box.append(el('div', { class: 'mb-confirm-acts' }, yes, cancel), err)
    item.append(box)
    return item
  }

  // Show this worker the job prices, or not (Liam, 2026-09-24). Off, they
  // are sent no price at all, and a cash payment asks them how much they took.
  if (row.role === 'worker') {
    const on = row.show_prices !== 0
    const sw = el('button', { type: 'button', class: `mb-switch${on ? ' mb-switch-on' : ''}`, role: 'switch', 'aria-checked': String(on), 'aria-label': `Show job prices to ${row.worker_name ?? row.email}` }, el('span', { class: 'mb-switch-knob', 'aria-hidden': 'true' }))
    sw.addEventListener('click', () => {
      sw.setAttribute('disabled', 'disabled')
      void mutate({
        label: `${on ? 'Hide' : 'Show'} prices: ${row.email}`,
        method: 'PATCH',
        path: `/api/users/${row.id}`,
        body: { show_prices: !on, version: row.version },
      }).then((ack) => {
        if (ack.ok) {
          void load(root)
          return
        }
        sw.removeAttribute('disabled')
        showFailure(ack, err, root)
      })
    })
    item.append(el('div', { class: 'mb-price-line' }, el('span', { class: 'mb-price-words' }, 'Sees job prices'), sw))
    if (!on) item.append(el('div', { class: 'mb-row-sub' }, 'Prices are hidden from them. When a customer pays cash, they are asked how much they took.'))
    item.append(payLine(row, root, err))
  }

  item.append(err)
  return item
}

// ---- the screen ------------------------------------------------------------

function render(root: HTMLElement): void {
  clear(root)
  // One lemon button on the page, "+ Add someone", opening a short pop-up with the same three boxes as before.
  const add = el('button', { type: 'button', class: 'btn-primary people-add-btn' }, '+ Add someone')
  add.addEventListener('click', () => openAdd(root, add))
  root.append(
    el(
      'div',
      { class: 'page-heading people-heading' },
      el(
        'div',
        {},
        el('h2', {}, 'People'),
        el('p', { class: 'muted' }, 'Everyone here signs in with a one-time code sent to their email. No password to remember.'),
      ),
      add,
    ),
  )

  if (rows.length === 0) {
    root.append(el('p', { class: 'empty-note' }, 'No one is set up on this round yet.'))
  } else {
    const list = el('div', { class: 'mb-list people-grid' })
    for (const row of rows) list.append(renderRow(row, root))
    root.append(list)
  }
}

// Also Your business's "+ Add someone" (0097; Liam: "it will be linked to the other sections where i can add a worker"):
// the same box, and that page draws itself again after (reload).
export function openAdd(root: HTMLElement | null, trigger: HTMLElement, reload?: () => void): void {
  const dialog = el('dialog', { class: 'people-add-dialog', 'aria-label': 'Add someone' }) as HTMLDialogElement
  const cancel = el('button', { type: 'button', class: 'btn-secondary' }, 'Cancel')
  const form = renderAdd(root, (held) => {
    dialog.close()
    if (held) toast('No signal. They will be added once your phone is back online.')
  }, reload)
  form.querySelector('.people-add-acts')?.append(cancel)
  cancel.addEventListener('click', () => dialog.close())
  // Going to another page closes it, so it never sits over a different screen (Codex, 15 Oct 2026).
  const leave = (): void => dialog.close()
  window.addEventListener('hashchange', leave)
  dialog.addEventListener('close', () => { window.removeEventListener('hashchange', leave); dialog.remove(); if (trigger.isConnected) trigger.focus() })
  dialog.append(el('h3', {}, 'Add someone'), ...(worksAlone() ? [el('p', { class: 'muted people-add-alone' }, ADD_TURNS_ON)] : []), form)
  document.body.append(dialog)
  dialog.showModal()
  form.querySelector<HTMLInputElement>('input[type=email]')?.focus()
}

async function load(root?: HTMLElement): Promise<void> {
  const gen = routeGen
  const sequence = ++requestSequence
  const target = root ?? document.querySelector<HTMLElement>('#screen')!
  try {
    const data = await api<{ users: PersonRow[] }>('/api/users', 'users')
    if (gen !== routeGen || sequence !== requestSequence) return
    rows = data.users
    editId = null
    confirmRemoveId = null
    render(target)
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence) return
    const cached = readCache<{ users: PersonRow[] }>('users')
    if (cached) {
      rows = cached.data.users
      render(target)
      loadError(target, (err as Error).message, () => void load(), cached.at)
    } else {
      clear(target)
      loadError(target, (err as Error).message, () => void load())
    }
  }
}

export function mountPeople(): void {
  editId = null
  confirmRemoveId = null
  void load()
}

export function peopleOnDataChanged(): void {
  void load()
}

// How a worker is paid (0064; Liam, 12 Oct 2026: "we set it so we can select an amount or commission ie for sendi its
// set at 170"): a day rate, or a share of the work they did that day. What their invoice asks for when they finish a day.
// Also on Your business, "How your people are paid" (0097): one setting, changed in either place. A commission can have a
// minimum a day (Liam: "% with a minimum"), counted on a day they did work.
export function payLine(row: PersonRow, root: HTMLElement | (() => void), err: HTMLElement): HTMLElement {
  const reload = (): void => { if (typeof root === 'function') root(); else void load(root) }
  const box = el('div', { class: 'mb-pay' })
  const said = row.pay_kind === 'day' && row.pay_amount !== null && row.pay_amount !== undefined ? `${money(row.pay_amount)} a day`
    : row.pay_kind === 'commission' && row.pay_amount !== null && row.pay_amount !== undefined ? `${row.pay_amount}% commission${row.pay_min_pence ? `, at least ${money(row.pay_min_pence)}` : ''}` : 'Not set'
  const show = (): void => {
    clear(box)
    // The whole line is the button (People A, 15 Oct 2026): "Pay  £170 a day ›".
    const edit = el('button', { type: 'button', class: 'mb-price-line people-pay-line', 'aria-label': `Change pay for ${row.worker_name ?? row.email}: ${said}` },
      el('span', { class: 'mb-price-words' }, 'Pay'), el('span', { class: 'mb-pay-said' }, said, el('span', { 'aria-hidden': 'true' }, ' ›')))
    edit.addEventListener('click', editor)
    box.append(edit)
    if (said === 'Not set') box.append(el('div', { class: 'mb-row-sub' }, 'Set it and their invoice asks for it when they finish a day.'))
  }
  const editor = (): void => {
    clear(box)
    let kind: 'day' | 'commission' = row.pay_kind === 'commission' ? 'commission' : 'day'
    const id = `pay-${row.id}`
    const amount = el('input', { id, type: 'number', inputmode: 'decimal', min: '0', step: '0.01', class: 'mb-pay-amount' }) as HTMLInputElement
    const label = el('label', { for: id, class: 'mb-pay-label' })
    const setKind = (k: 'day' | 'commission'): void => {
      kind = k
      label.textContent = k === 'day' ? `A day’s work (${currencySymbol()})` : 'Share of the work they do (%)'
      amount.step = k === 'day' ? '0.01' : '1'
      for (const b of kinds.querySelectorAll<HTMLButtonElement>('button')) { const on = b.dataset.kind === k; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', String(on)) }
    }
    const kinds = el('div', { class: 'mb-pay-kinds', role: 'group', 'aria-label': 'How they are paid' })
    for (const [k, words] of [['day', 'Day rate'], ['commission', 'Commission']] as const) {
      const b = el('button', { type: 'button', class: 'btn-secondary', 'data-kind': k }, words)
      b.addEventListener('click', () => setKind(k))
      kinds.append(b)
    }
    if (row.pay_amount !== null && row.pay_amount !== undefined) amount.value = row.pay_kind === 'day' ? (row.pay_amount / 100).toFixed(2) : String(row.pay_amount)
    // A commission's minimum a day, optional.
    const minId = `pay-min-${row.id}`
    const minBox = el('input', { id: minId, type: 'number', inputmode: 'decimal', min: '0', step: '0.01', class: 'mb-pay-amount', placeholder: 'None' }) as HTMLInputElement
    if (row.pay_min_pence) minBox.value = (row.pay_min_pence / 100).toFixed(2)
    const minField = el('div', { class: 'mb-pay-min' }, el('label', { for: minId, class: 'mb-pay-label' }, `At least, on a day they work (${currencySymbol()}, optional)`), minBox)
    const showMin = (): void => { minField.hidden = kind !== 'commission' }
    for (const b of kinds.querySelectorAll<HTMLButtonElement>('button')) b.addEventListener('click', showMin)
    setKind(kind)
    showMin()
    const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save')
    const cancel = el('button', { type: 'button', class: 'btn-secondary' }, 'Cancel')
    const clearIt = el('button', { type: 'button', class: 'link-button' }, 'No pay set')
    const send = (body: Record<string, unknown>): void => {
      save.setAttribute('disabled', 'disabled')
      void mutate({ label: `Pay: ${row.worker_name ?? row.email}`, method: 'PATCH', path: `/api/users/${row.id}`, body: { ...body, version: row.version } })
        .then((ack) => { if (ack.ok) { reload(); return } save.removeAttribute('disabled'); showFailure(ack, err, root) })
    }
    save.addEventListener('click', () => {
      const n = Number(amount.value)
      if (!amount.value.trim() || !Number.isFinite(n) || n < 0 || (kind === 'commission' && n > 100)) { amount.focus(); return }
      const m = minBox.value.trim() ? Number(minBox.value) : null
      if (kind === 'commission' && m !== null && (!Number.isFinite(m) || m < 0)) { minBox.focus(); return }
      send({ pay_kind: kind, pay_amount: kind === 'day' ? Math.round(n * 100) : Math.round(n), ...(kind === 'commission' ? { pay_min_pence: m === null ? null : Math.round(m * 100) } : {}) })
    })
    cancel.addEventListener('click', show)
    clearIt.addEventListener('click', () => send({ pay_kind: null, pay_amount: null }))
    box.append(el('div', { class: 'mb-price-words' }, 'How they are paid'), kinds, label, amount, minField, el('div', { class: 'mb-row-acts' }, save, cancel, clearIt))
    amount.focus()
  }
  show()
  return box
}
